import { useState, useEffect } from 'react';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import PersonFilterBar from '../components/PersonFilterBar';
import PetBadge from '../components/PetBadge';
import { formatDateOnly } from '../utils/dates';
import { wishOwner, ownerKey } from '../utils/wishOwner';

/**
 * My Purchases — the gifts the logged-in user has bought for others.
 * (These are the buyer's own records; owner-blindness doesn't apply here.)
 *
 * The recipient filter answers "did I get enough for Dad?". It keys off the
 * wish's DISPLAY owner via wishOwner(), so a gift bought for a pet files under
 * the pet rather than its caretaker.
 */
export default function MyPurchases() {
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterOwnerKey, setFilterOwnerKey] = useState(null);

    useEffect(() => {
        async function load() {
            try {
                const res = await api.get('/purchases/mine');
                setPurchases(res.data);
            } catch (err) {
                setError('Failed to load your purchases.');
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    // PersonFilterBar reads a wish, so feed it the wishes and map back to the
    // purchase rows that wrap them.
    const purchasedWishes = purchases.map((p) => p.wish).filter(Boolean);
    const ownerStillPresent = purchasedWishes.some((w) => ownerKey(w) === filterOwnerKey);
    const activeOwnerKey = ownerStillPresent ? filterOwnerKey : null;
    const visible = activeOwnerKey
        ? purchases.filter((p) => p.wish && ownerKey(p.wish) === activeOwnerKey)
        : purchases;

    return (
        <AppLayout maxWidth="max-w-4xl">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">My Purchases</h2>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>
                )}

                {!loading && purchases.length > 0 && (
                    <PersonFilterBar
                        wishes={purchasedWishes}
                        value={activeOwnerKey}
                        onChange={setFilterOwnerKey}
                    />
                )}

                {loading ? (
                    <div className="text-gray-600">Loading...</div>
                ) : purchases.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                        You haven't purchased any gifts yet.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {visible.map((p) => {
                            // Display owner, not p.wish.user: a gift bought for a
                            // pet should name the PET, not its caretaker.
                            const owner = p.wish ? wishOwner(p.wish) : null;
                            return (
                            <div key={p.id} className="bg-white rounded-lg shadow-sm p-4">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h3 className="font-semibold text-gray-900">{p.wish?.title || 'Wish'}</h3>
                                        <p className="text-sm text-gray-500 flex items-center gap-1.5">
                                            <span>
                                                For {owner?.name || 'someone'}
                                                {p.group?.name ? ` · ${p.group.name}` : ''}
                                            </span>
                                            {owner?.isPet && <PetBadge species={owner.species} />}
                                        </p>
                                    </div>
                                    <span className="text-xs text-gray-400">
                                        {p.purchased_at ? new Date(p.purchased_at).toLocaleDateString() : ''}
                                    </span>
                                </div>
                                {p.private_note && <p className="text-sm text-gray-600 mt-2">Note: {p.private_note}</p>}
                                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                    {p.reveal_date && (
                                        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                                            Reveals {formatDateOnly(p.reveal_date)}
                                        </span>
                                    )}
                                    {p.revealed_at && (
                                        <span className="px-2 py-0.5 rounded bg-green-100 text-green-700">Revealed</span>
                                    )}
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
        </AppLayout>
    );
}
